import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
// @mui
import { Box } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
// utils
import { bgGradient } from "@/core/utils/cssStyles";
// components
import Carousel, { CarouselArrowIndex } from "@/core/components/carousel";
import Image from "@/core/components/image";
import Lightbox from "@/core/components/lightbox";

// ----------------------------------------------------------------------

const THUMB_SIZE = 64;

const StyledThumbnailsContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "length",
})(({ length, theme }) => ({
  margin: theme.spacing(0, "auto"),
  position: "relative",

  "& .slick-slide": {
    opacity: 0.48,
    "&.slick-current": {
      opacity: 1,
    },
    "& > div": {
      padding: theme.spacing(0, 0.75),
    },
  },

  ...(length === 1 && {
    maxWidth: THUMB_SIZE * 1 + 16,
  }),
  ...(length === 2 && {
    maxWidth: THUMB_SIZE * 2 + 32,
  }),
  ...((length === 3 || length === 4) && {
    maxWidth: THUMB_SIZE * 3 + 48,
  }),
  ...(length >= 5 && {
    maxWidth: THUMB_SIZE * 6,
  }),
  ...(length > 2 && {
    "&:before, &:after": {
      ...bgGradient({
        direction: "to left",
        startColor: `${alpha(theme.palette.background.default, 0)} 0%`,
        endColor: `${theme.palette.background.default} 100%`,
      }),
      top: 0,
      zIndex: 9,
      content: "''",
      height: "100%",
      position: "absolute",
      width: (THUMB_SIZE * 2) / 3,
    },
    "&:after": {
      right: 0,
      transform: "scaleX(-1)",
    },
  }),
}));

// ----------------------------------------------------------------------

ProductDetailsCarousel.propTypes = {
  product: PropTypes.object,
};

export default function ProductDetailsCarousel({ product }) {
  const theme = useTheme();

  const carousel1 = useRef(null);

  const carousel2 = useRef(null);

  const [nav1, setNav1] = useState();

  const [nav2, setNav2] = useState();

  const [currentIndex, setCurrentIndex] = useState(0);

  const [selectedImage, setSelectedImage] = useState(-1);

  const imagesLightbox = product.files.map((img) => ({ src: img }));

  const handleOpenLightbox = (imageUrl) => {
    const imageIndex = imagesLightbox.findIndex(
      (image) => image.src === imageUrl
    );
    setSelectedImage(imageIndex);
  };

  const handleCloseLightbox = () => {
    setSelectedImage(-1);
  };

  const carouselSettings1 = {
    dots: false,
    arrows: false,
    slidesToShow: 1,
    draggable: false,
    slidesToScroll: 1,
    adaptiveHeight: true,
    beforeChange: (current, next) => setCurrentIndex(next),
  };

  const carouselSettings2 = {
    dots: false,
    arrows: false,
    centerMode: true,
    swipeToSlide: true,
    focusOnSelect: true,
    variableWidth: true,
    centerPadding: "0px",
    slidesToShow: product.files.length > 3 ? 3 : product?.files.length,
  };

  useEffect(() => {
    if (carousel1.current) {
      setNav1(carousel1.current);
    }
    if (carousel2.current) {
      setNav2(carousel2.current);
    }
  }, []);

  useEffect(() => {
    carousel1.current?.slickGoTo(currentIndex);
  }, [currentIndex]);

  const handlePrev = () => {
    carousel2.current?.slickPrev();
  };

  const handleNext = () => {
    carousel2.current?.slickNext();
  };

  const renderLargeImg = (
    <Box
      sx={{ mb: 3, borderRadius: 2, overflow: "hidden", position: "relative" }}
    >
      <Carousel
        {...carouselSettings1}
        asNavFor={nav2}
        ref={carousel1}
      >
        {product.files.map((img) => (
          <Image
            key={img?.medium?.url}
            alt="product"
            src={img?.medium?.url}
            ratio="1/1"
            onClick={() => handleOpenLightbox(img?.medium?.url)}
            sx={{ cursor: "zoom-in" }}
          />
        ))}
      </Carousel>

      <CarouselArrowIndex
        index={currentIndex}
        total={product.files.length}
        onNext={handleNext}
        onPrevious={handlePrev}
      />
    </Box>
  );

  const renderThumbnails = (
    <StyledThumbnailsContainer length={product.files.length}>
      <Carousel
        {...carouselSettings2}
        asNavFor={nav1}
        ref={carousel2}
      >
        {product.files.map((img, index) => (
          <Image
            key={img?.thumbnail?.url}
            disabledEffect
            alt="thumbnail"
            src={img?.thumbnail?.url}
            sx={{
              width: THUMB_SIZE,
              height: THUMB_SIZE,
              borderRadius: 1.5,
              cursor: "pointer",
              ...(currentIndex === index && {
                border: `solid 2px ${theme.palette.primary.main}`,
              }),
            }}
          />
        ))}
      </Carousel>
    </StyledThumbnailsContainer>
  );

  return (
    <>
      <Box
        sx={{
          "& .slick-slide": {
            float: theme.direction === "rtl" ? "right" : "left",
          },
        }}
      >
        {renderLargeImg}

        {renderThumbnails}
      </Box>

      <Lightbox
        index={selectedImage}
        slides={imagesLightbox}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
        onGetCurrentIndex={(index) => setCurrentIndex(index)}
      />
    </>
  );
}
